import React from 'react';
import MarkdownRenderer from '../common/MarkdownRenderer';

// Visual styling per actor so the transcript reads like a courtroom record.
const ACTOR_META = {
  judge: { label: 'Judge', wrap: 'items-center', bubble: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800', name: 'text-amber-700 dark:text-amber-300' },
  opposing: { label: 'Opposing Counsel', wrap: 'items-start', bubble: 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-700', name: 'text-gray-700 dark:text-gray-300' },
  witness: { label: 'Witness', wrap: 'items-start', bubble: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', name: 'text-purple-700 dark:text-purple-300' },
  student: { label: 'You', wrap: 'items-end', bubble: 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800', name: 'text-primary-700 dark:text-primary-300' },
};

/**
 * Renders the ordered list of trial turns.
 * Props: turns: [{ id, index, phase, actor, content, citations, agent_name, created_at }]
 */
const TrialTranscript = ({ turns = [] }) => {
  if (!turns.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
        The proceedings will appear here once they begin.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {turns.map((turn) => {
        const meta = ACTOR_META[turn.actor] || ACTOR_META.opposing;
        const isStudent = turn.actor === 'student';
        return (
          <div key={turn.id} className={`flex flex-col ${meta.wrap}`}>
            <div className={`max-w-full sm:max-w-[85%] rounded-lg border p-3 sm:p-4 ${meta.bubble}`}>
              <div className="flex items-center justify-between gap-3 mb-1">
                <span className={`text-xs font-semibold ${meta.name}`}>
                  {meta.label}
                  {turn.agent_name && !isStudent && (
                    <span className="ml-1 font-normal text-gray-400 dark:text-gray-500">· {turn.agent_name}</span>
                  )}
                </span>
                {turn.phase && (
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{turn.phase}</span>
                )}
              </div>
              <div className="text-sm">
                <MarkdownRenderer content={turn.content} />
              </div>
              {Array.isArray(turn.citations) && turn.citations.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">Citations</p>
                  <ul className="space-y-0.5">
                    {turn.citations.map((c, i) => (
                      <li key={i} className="text-[11px] text-gray-500 dark:text-gray-400 break-words">
                        {typeof c === 'string' ? c : c.title || c.citation || JSON.stringify(c)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TrialTranscript;
